import { useState, useEffect } from 'react';

export default function ComingSoon() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const targetDate = new Date('2025-06-01T00:00:00').getTime();

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const difference = targetDate - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new URLSearchParams();
      formData.append('name', name);
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d4es70miti2a337fgu40', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString()
      });

      if (response.ok) {
        setSubmitMessage('Thank you! We\'ll notify you when we launch.');
        setEmail('');
        setName('');
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: 'rgb(24, 76, 120)' }}>
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-96 h-96 bg-blue-400/10 rounded-full blur-3xl -top-48 -left-48 animate-pulse"></div>
        <div className="absolute w-96 h-96 bg-blue-600/10 rounded-full blur-3xl -bottom-48 -right-48 animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl w-full">
          {/* Logo/Brand Area */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center mb-6 mt-16">
              <img 
                src="https://static.readdy.ai/image/48ec45a2b269994b7b5887dc2671984d/57977583a16d49505f4c824710adfc46.png" 
                alt="Logo" 
                className="w-64 h-auto"
              />
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Something <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-blue-100">Revolutionary</span> is Coming
            </h1>
            <p className="text-xl md:text-2xl text-gray-100 max-w-3xl mx-auto leading-relaxed">
              We're building a platform that will transform the way people engage with democratic processes. Get ready for a new era of civic participation.
            </p>
          </div>

          {/* Countdown Timer */}
          <div className="mb-16">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300">
                <div className="text-4xl md:text-6xl font-bold text-white mb-2">{timeLeft.days}</div>
                <div className="text-sm md:text-base text-gray-100 uppercase tracking-wider">Days</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300">
                <div className="text-4xl md:text-6xl font-bold text-white mb-2">{timeLeft.hours}</div>
                <div className="text-sm md:text-base text-gray-100 uppercase tracking-wider">Hours</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300">
                <div className="text-4xl md:text-6xl font-bold text-white mb-2">{timeLeft.minutes}</div>
                <div className="text-sm md:text-base text-gray-100 uppercase tracking-wider">Minutes</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300">
                <div className="text-4xl md:text-6xl font-bold text-white mb-2">{timeLeft.seconds}</div>
                <div className="text-sm md:text-base text-gray-100 uppercase tracking-wider">Seconds</div>
              </div>
            </div>
          </div>

          {/* Features Preview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16 max-w-4xl mx-auto">
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300">
              <div className="w-14 h-14 bg-blue-400/20 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-blue-200 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Secure & Transparent</h3>
              <p className="text-gray-200 leading-relaxed">
                Built with cutting-edge security and complete transparency at its core.
              </p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300">
              <div className="w-14 h-14 bg-blue-400/20 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-group-line text-blue-200 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Community Driven</h3>
              <p className="text-gray-200 leading-relaxed">
                Empowering communities to make their voices heard and drive real change.
              </p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:bg-white/10 transition-all duration-300">
              <div className="w-14 h-14 bg-blue-400/20 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-lightbulb-line text-blue-200 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Innovation First</h3>
              <p className="text-gray-200 leading-relaxed">
                Leveraging modern technology to solve age-old challenges in new ways.
              </p>
            </div>
          </div>

          {/* Email Signup Form */}
          <div className="max-w-2xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 md:p-10 border border-white/20 shadow-2xl">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3 text-center">
                Be the First to Know
              </h2>
              <p className="text-gray-100 mb-8 text-center">
                Join our exclusive early access list and get notified the moment we launch.
              </p>
              
              <form onSubmit={handleSubmit} data-readdy-form id="early_access_form">
                <div className="space-y-4 mb-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-100 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-transparent transition-all text-sm"
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-100 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-transparent transition-all text-sm"
                      placeholder="Enter your email address"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 disabled:from-gray-500 disabled:to-gray-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Notify Me at Launch'}
                </button>

                {submitMessage && (
                  <div className={`mt-4 p-4 rounded-lg text-center ${
                    submitMessage.includes('Thank you') 
                      ? 'bg-green-500/20 text-green-200 border border-green-400/30' 
                      : 'bg-red-500/20 text-red-200 border border-red-400/30'
                  }`}>
                    {submitMessage}
                  </div>
                )}

                <p className="text-sm text-gray-200 mt-4 text-center">
                  We respect your privacy. No spam, ever. Unsubscribe anytime.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 py-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center">
            <p className="text-gray-100 text-sm">
              © 2025 All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
